// src/hooks/useStorageData.ts

import { useState, useCallback } from "react";
import { DatabaseInfo, SyncQueueStatus } from "../types/storage.types";
import { withTimeout } from "../utils/storage.utils";

interface UseStorageDataReturn {
  databases: DatabaseInfo[];
  syncQueue: SyncQueueStatus;
  issues: string[];
  loadDatabases: () => Promise<void>;
  loadSyncStatus: (swController?: ServiceWorker) => Promise<void>;
  addIssue: (issue: string) => void;
  clearIssues: () => void;
}

const getServiceWorker = async (): Promise<ServiceWorker | undefined> => {
  if (!navigator.serviceWorker) return undefined;

  try {
    const registration = await navigator.serviceWorker.ready;
    return registration.active || undefined;
  } catch (error) {
    console.warn("Error obteniendo Service Worker:", error);
    return undefined;
  }
};

const sendSWMessage = async <T>(
  swController: ServiceWorker | undefined,
  message: any,
  timeoutMs = 10000
): Promise<T> => {
  if (!swController) {
    throw new Error("Service Worker no disponible");
  }

  return new Promise((resolve, reject) => {
    const channel = new MessageChannel();

    const timeout = setTimeout(() => {
      reject(new Error(`Timeout esperando respuesta del SW (${timeoutMs}ms)`));
    }, timeoutMs);

    channel.port1.onmessage = (event) => {
      clearTimeout(timeout);

      if (event.data.type?.endsWith("_ERROR")) {
        reject(
          new Error(event.data.error || "Error desconocido del Service Worker")
        );
      } else {
        resolve(event.data as T);
      }
    };

    swController.postMessage(message, [channel.port2]);
  });
};

export const useStorageData = (): UseStorageDataReturn => {
  const [databases, setDatabases] = useState<DatabaseInfo[]>([]);
  const [syncQueue, setSyncQueue] = useState<SyncQueueStatus>({
    pendingPosts: 0,
    lastSyncStatus: null,
  });
  const [issues, setIssues] = useState<string[]>([]);

  const addIssue = useCallback((issue: string) => {
    setIssues((prev) => [...prev, issue]);
  }, []);

  const clearIssues = useCallback(() => {
    setIssues([]);
  }, []);

  const loadDatabases = useCallback(async () => {
    try {
      console.log(
        "🎯 Cargando información de bases de datos vía Service Worker..."
      );

      const swController = await getServiceWorker();
      if (!swController) {
        addIssue("API de bases de datos no soportada");
        setDatabases([]);
        return;
      }

      const response = await sendSWMessage<any>(
        swController,
        {
          type: "GET_DATABASE_INFO",
        },
        15000
      );

      if (response.type === "DATABASE_INFO_RESULT") {
        setDatabases(response.databases || []);
        console.log(
          "✅ Bases de datos cargadas vía SW:",
          response.databases?.length || 0
        );
      }
    } catch (error) {
      console.error(
        "❌ Error cargando información de bases de datos vía SW:",
        error
      );
      addIssue("Error listando bases de datos: " + (error as Error).message);
      setDatabases([]);
    }
  }, [addIssue]);

  const loadSyncStatus = useCallback(
    async (swController?: ServiceWorker) => {
      if (!swController) {
        console.log("⚠️ Service Worker no disponible para sync check");
        return;
      }

      try {
        console.log("🎯 Verificando elementos pendientes...");

        // Verificar POST pendientes
        const postPromise = withTimeout(
          new Promise<void>((resolve) => {
            const channel = new MessageChannel();
            channel.port1.onmessage = (event) => {
              if (event.data.type === "PENDING_ITEMS_RESULT") {
                setSyncQueue((prev) => ({
                  ...prev,
                  pendingPosts: event.data.hasPending ? 1 : 0,
                }));
                resolve();
              }
            };
            swController.postMessage({ type: "CHECK_PENDING_ITEMS" }, [
              channel.port2,
            ]);
          }),
          10000,
          "check pending posts"
        );

        await Promise.allSettled([postPromise]);
        console.log("✅ Verificación de pendientes completada");
      } catch (error) {
        console.warn("⚠️ Error verificando elementos pendientes:", error);
        addIssue("Error verificando elementos de sincronización");
      }
    },
    [addIssue]
  );

  // Función para cargar thumbnail específico

  return {
    databases,
    syncQueue,
    issues,
    loadDatabases,
    loadSyncStatus,
    addIssue,
    clearIssues,
  };
};
